<template>
  <div class="todo-layout">
    <!-- Sidebar -->
    <Sidebar :isAgent="isAgent" />

    <!-- Main Content -->
    <div class="main-content">
      <!-- Top Header -->
      <TopHeader />

      <!-- Page Content -->
      <main class="page-content">
        <div class="todo-container">
          <!-- Page Header -->
          <div class="page-header-minimal">
            <button @click="showCreateModal = true" class="btn-primary">
              <svg class="btn-icon" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2c5.514 0 10 4.486 10 10s-4.486 10-10 10-10-4.486-10-10 4.486-10 10-10zm0 1c-4.962 0-9 4.038-9 9s4.038 9 9 9 9-4.038 9-9-4.038-9-9-9zm-1 4h2v4h4v2h-4v4h-2v-4h-4v-2h4v-4z"/>
              </svg>
              Add Task
            </button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="loading-state">
            <div class="loading-spinner"></div>
            <p>Loading tasks...</p>
          </div>

          <!-- Kanban Board -->
          <div v-else class="kanban-board">
            <!-- To Do Column -->
            <div class="kanban-column todo-column">
              <div class="column-header">
                <div class="column-title">
                  <span class="column-icon">📝</span>
                  <span class="column-name">To Do</span>
                  <span class="task-count">{{ todoTasks.length }}</span>
                </div>
              </div>
              <div
                class="column-content drop-zone"
                @dragover="onDragOver"
                @drop="onDrop($event, 'pending')"
                :class="{ 'drag-over': isDragging && draggedTask?.status !== 'pending' }"
              >
                <div v-if="todoTasks.length === 0" class="empty-column">
                  <div class="empty-icon">📝</div>
                  <p>No tasks to do</p>
                </div>
                <div v-else class="tasks-list">
                  <div
                    v-for="task in todoTasks"
                    :key="task.id"
                    class="task-card"
                    :class="{
                      'pinned': task.pinned,
                      'dragging': isDragging && draggedTask?.id === task.id,
                      'urgent-deadline': getDeadlineUrgencyClass(task.deadline) === 'deadline-overdue' || getDeadlineUrgencyClass(task.deadline) === 'deadline-today'
                    }"
                    draggable="true"
                    @dragstart="onDragStart($event, task)"
                    @dragend="onDragEnd"
                  >
                    <div class="task-header">
                      <div class="task-priority" :class="`priority-${task.priority}`"></div>
                      <div class="task-actions">
                        <button v-if="task.pinned" @click="togglePin(task.id)" class="task-btn pinned">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z"/>
                          </svg>
                        </button>
                        <button v-else @click="togglePin(task.id)" class="task-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z"/>
                          </svg>
                        </button>
                        <button @click="editTask(task)" class="task-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button @click="deleteTask(task.id)" class="task-btn delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h4 class="task-title">{{ task.title }}</h4>
                    <p v-if="task.description" class="task-description">{{ task.description }}</p>
                    <div class="task-footer">
                      <div class="task-meta">
                        <span v-if="task.deadline" class="task-due-date" :class="getDeadlineUrgencyClass(task.deadline)">
                          📅 {{ formatDate(task.deadline) }} {{ getDeadlineStatus(task.deadline) }}
                        </span>
                        <span v-if="task.assigned_to" class="task-assignee">
                          👤 {{ getAgentName(task.assigned_to) }}
                        </span>
                        <span v-if="task.customer_name" class="task-customer">
                          👥 {{ task.customer_name }} {{ task.customer_phone ? `(${task.customer_phone})` : '' }}
                        </span>
                      </div>
                      <div class="task-status-actions">
                        <button @click="changeStatus(task.id, 'in_progress')" class="status-btn progress">
                          ▶️
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- In Progress Column -->
            <div class="kanban-column progress-column">
              <div class="column-header">
                <div class="column-title">
                  <span class="column-icon">⏳</span>
                  <span class="column-name">In Progress</span>
                  <span class="task-count">{{ progressTasks.length }}</span>
                </div>
              </div>
              <div
                class="column-content drop-zone"
                @dragover="onDragOver"
                @drop="onDrop($event, 'in_progress')"
                :class="{ 'drag-over': isDragging && draggedTask?.status !== 'in_progress' }"
              >
                <div v-if="progressTasks.length === 0" class="empty-column">
                  <div class="empty-icon">⏳</div>
                  <p>No tasks in progress</p>
                </div>
                <div v-else class="tasks-list">
                  <div
                    v-for="task in progressTasks"
                    :key="task.id"
                    class="task-card in-progress"
                    :class="{
                      'pinned': task.pinned,
                      'dragging': isDragging && draggedTask?.id === task.id,
                      'urgent-deadline': getDeadlineUrgencyClass(task.deadline) === 'deadline-overdue' || getDeadlineUrgencyClass(task.deadline) === 'deadline-today'
                    }"
                    draggable="true"
                    @dragstart="onDragStart($event, task)"
                    @dragend="onDragEnd"
                  >
                    <div class="task-header">
                      <div class="task-priority" :class="`priority-${task.priority}`"></div>
                      <div class="task-actions">
                        <button v-if="task.pinned" @click="togglePin(task.id)" class="task-btn pinned">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z"/>
                          </svg>
                        </button>
                        <button v-else @click="togglePin(task.id)" class="task-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                            <path d="M16,12V4H17V2H7V4H8V12L6,14V16H11.2V22H12.8V16H18V14L16,12Z"/>
                          </svg>
                        </button>
                        <button @click="editTask(task)" class="task-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button @click="deleteTask(task.id)" class="task-btn delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h4 class="task-title">{{ task.title }}</h4>
                    <p v-if="task.description" class="task-description">{{ task.description }}</p>
                    <div class="task-footer">
                      <div class="task-meta">
                        <span v-if="task.deadline" class="task-due-date" :class="getDeadlineUrgencyClass(task.deadline)">
                          📅 {{ formatDate(task.deadline) }} {{ getDeadlineStatus(task.deadline) }}
                        </span>
                        <span v-if="task.assigned_to" class="task-assignee">
                          👤 {{ getAgentName(task.assigned_to) }}
                        </span>
                        <span v-if="task.customer_name" class="task-customer">
                          👥 {{ task.customer_name }} {{ task.customer_phone ? `(${task.customer_phone})` : '' }}
                        </span>
                      </div>
                      <div class="task-status-actions">
                        <button @click="changeStatus(task.id, 'pending')" class="status-btn back">
                          ⬅️
                        </button>
                        <button @click="changeStatus(task.id, 'completed')" class="status-btn complete">
                          ✅
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Completed Column -->
            <div class="kanban-column completed-column">
              <div class="column-header">
                <div class="column-title">
                  <span class="column-icon">✅</span>
                  <span class="column-name">Completed</span>
                  <span class="task-count">{{ completedTasks.length }}</span>
                </div>
              </div>
              <div
                class="column-content drop-zone"
                @dragover="onDragOver"
                @drop="onDrop($event, 'completed')"
                :class="{ 'drag-over': isDragging && draggedTask?.status !== 'completed' }"
              >
                <div v-if="completedTasks.length === 0" class="empty-column">
                  <div class="empty-icon">✅</div>
                  <p>No completed tasks</p>
                </div>
                <div v-else class="tasks-list">
                  <div
                    v-for="task in completedTasks"
                    :key="task.id"
                    class="task-card completed"
                    :class="{
                      'pinned': task.pinned,
                      'dragging': isDragging && draggedTask?.id === task.id,
                      'urgent-deadline': getDeadlineUrgencyClass(task.deadline) === 'deadline-overdue' || getDeadlineUrgencyClass(task.deadline) === 'deadline-today'
                    }"
                    draggable="true"
                    @dragstart="onDragStart($event, task)"
                    @dragend="onDragEnd"
                  >
                    <div class="task-header">
                      <div class="task-priority" :class="`priority-${task.priority}`"></div>
                      <div class="task-actions">
                        <button @click="editTask(task)" class="task-btn">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                          </svg>
                        </button>
                        <button @click="deleteTask(task.id)" class="task-btn delete">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                          </svg>
                        </button>
                      </div>
                    </div>
                    <h4 class="task-title">{{ task.title }}</h4>
                    <p v-if="task.description" class="task-description">{{ task.description }}</p>
                    <div class="task-footer">
                      <div class="task-meta">
                        <span v-if="task.due_date" class="task-due-date completed-date">
                          📅 {{ formatDate(task.due_date) }}
                        </span>
                        <span v-if="task.assigned_to" class="task-assignee">
                          👤 {{ getAgentName(task.assigned_to) }}
                        </span>
                        <span v-if="task.customer_name" class="task-customer">
                          👥 {{ task.customer_name }} {{ task.customer_phone ? `(${task.customer_phone})` : '' }}
                        </span>
                      </div>
                      <div class="task-status-actions">
                        <button @click="changeStatus(task.id, 'in_progress')" class="status-btn reopen">
                          ⬅️
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Task Creation/Edit Modal -->
    <div v-if="showCreateModal || showEditModal" class="modal-overlay" @click="closeModal">
      <div class="task-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">
            {{ showEditModal ? 'Edit Task' : 'Create New Task' }}
          </h3>
          <button @click="closeModal" class="modal-close">✖️</button>
        </div>
        <form @submit.prevent="saveTask" class="task-form">
          <div class="form-group">
            <label for="taskTitle" class="form-label">Task Title</label>
            <input
              id="taskTitle"
              v-model="taskForm.title"
              type="text"
              class="form-input"
              placeholder="Enter task title..."
              required
            />
          </div>

          <div class="form-group">
            <label for="taskDescription" class="form-label">Description</label>
            <textarea
              id="taskDescription"
              v-model="taskForm.description"
              class="form-textarea"
              rows="3"
              placeholder="Enter task description..."
            ></textarea>
          </div>

          <div class="form-group">
            <label for="taskPriority" class="form-label">Priority</label>
            <select id="taskPriority" v-model="taskForm.priority" class="form-select">
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="taskDeadline" class="form-label">Deadline</label>
              <input
                id="taskDeadline"
                v-model="taskForm.deadline"
                type="datetime-local"
                class="form-input"
              />
            </div>

            <div class="form-group">
              <label for="taskAssignee" class="form-label">Assign To Agent</label>
              <select
                id="taskAssignee"
                v-model="taskForm.agent_id"
                class="form-select"
              >
                <option value="">Select an agent...</option>
                <option
                  v-for="agent in agents"
                  :key="agent.id"
                  :value="agent.id"
                >
                  {{ agent.name }}
                </option>
              </select>
            </div>
          </div>

          <div class="form-group">
            <label for="taskCustomer" class="form-label">Customer</label>
            <div class="searchable-dropdown" ref="customerDropdown">
              <input
                id="taskCustomer"
                v-model="customerSearchQuery"
                @input="onCustomerSearch"
                @focus="showCustomerDropdown = true"
                @blur="handleCustomerBlur"
                type="text"
                class="form-input"
                placeholder="Search customers by name or phone..."
                autocomplete="off"
              />
              <div v-if="showCustomerDropdown && filteredCustomers.length > 0" class="dropdown-options">
                <div
                  v-for="customer in filteredCustomers"
                  :key="customer.id"
                  class="dropdown-option"
                  @mousedown="selectCustomer(customer)"
                >
                  <strong>{{ customer.full_name }}</strong>
                  <span class="customer-phone">{{ customer.phone_number || 'No phone' }}</span>
                </div>
              </div>
              <div v-if="showCustomerDropdown && customerSearchQuery && filteredCustomers.length === 0" class="dropdown-no-results">
                No customers found
              </div>
            </div>
          </div>

          <div class="modal-actions">
            <button type="button" @click="closeModal" class="btn-secondary">
              Cancel
            </button>
            <button type="submit" class="btn-primary" :disabled="saving">
              <span v-if="saving" class="loading-spinner-small"></span>
              {{ saving ? 'Saving...' : (showEditModal ? 'Update Task' : 'Create Task') }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Success Snackbar -->
    <div v-if="showSuccessNotification" class="snackbar success-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">✅</span>
        <span class="snackbar-message">{{ successMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>

    <!-- Error Snackbar -->
    <div v-if="showErrorNotification" class="snackbar error-snackbar">
      <div class="snackbar-content">
        <span class="snackbar-icon">❌</span>
        <span class="snackbar-message">{{ errorMessage }}</span>
      </div>
      <button @click="hideNotification" class="snackbar-close">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import Sidebar from '../components/Sidebar.vue'
import TopHeader from '../components/TopHeader.vue'
import { DashboardService } from '../services/dashboard'

export default {
  name: 'TodoView',
  components: {
    Sidebar,
    TopHeader
  },
  data () {
    return {
      isAgent: !!localStorage.getItem('agent_token'),
      loading: true,
      saving: false,
      todos: [],
      agents: [],
      customers: [],
      showCreateModal: false,
      showEditModal: false,
      editingTask: null,
      taskForm: {
        title: '',
        description: '',
        priority: 'medium',
        deadline: '',
        customer_id: null,
        agent_id: null,
        pinned: 0
      },
      showSuccessNotification: false,
      showErrorNotification: false,
      successMessage: '',
      errorMessage: '',
      notificationTimeout: null,
      draggedTask: null,
      isDragging: false,
      customerSearchQuery: '',
      showCustomerDropdown: false,
      selectedCustomer: null
    }
  },
  computed: {
    todoTasks () {
      return this.todos.filter(task => task.status === 'pending').sort((a, b) => {
        // Sort by pinned first, then by priority
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1

        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 }
        return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2)
      })
    },
    progressTasks () {
      return this.todos.filter(task => task.status === 'in_progress').sort((a, b) => {
        // Sort by pinned first, then by priority
        if (a.pinned && !b.pinned) return -1
        if (!a.pinned && b.pinned) return 1

        const priorityOrder = { urgent: 4, high: 3, medium: 2, low: 1 }
        return (priorityOrder[b.priority] || 2) - (priorityOrder[a.priority] || 2)
      })
    },
    completedTasks () {
      return this.todos.filter(task => task.status === 'completed').sort((a, b) => {
        // Sort by completion date (newest first)
        return new Date(b.updated_at) - new Date(a.updated_at)
      })
    },
    filteredCustomers () {
      if (!this.customerSearchQuery || this.customerSearchQuery.length < 1) {
        return this.customers
      }
      const query = this.customerSearchQuery.toLowerCase().trim()
      return this.customers.filter(customer => {
        const name = customer.full_name?.toLowerCase() || ''
        const phone = customer.phone_number?.toString() || ''
        const email = customer.email?.toLowerCase() || ''

        return name.includes(query) ||
               phone.includes(query) ||
               email.includes(query) ||
               name.split(' ').some(word => word.startsWith(query))
      })
    }
  },
  async mounted () {
    await Promise.all([
      this.loadTodos(),
      this.loadAgents(),
      this.loadCustomers()
    ])
  },
  methods: {
    // Drag and Drop Methods
    onDragStart (event, task) {
      this.draggedTask = task
      this.isDragging = true
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/html', event.target.outerHTML)
    },

    onDragEnd () {
      this.draggedTask = null
      this.isDragging = false
    },

    onDragOver (event) {
      event.preventDefault()
      event.dataTransfer.dropEffect = 'move'
    },

    async onDrop (event, newStatus) {
      event.preventDefault()

      if (!this.draggedTask || this.draggedTask.status === newStatus) {
        this.onDragEnd()
        return
      }

      try {
        await this.changeStatus(this.draggedTask.id, newStatus)
      } catch (error) {
        console.error('Error updating task status via drag and drop:', error)
        this.displayErrorNotification('Error moving task')
      } finally {
        this.onDragEnd()
      }
    },

    async loadTodos () {
      try {
        this.loading = true
        const result = await DashboardService.getTodos()

        if (result.success) {
          this.todos = result.todos
        } else {
          console.error('Failed to load todos:', result.error)
          this.displayErrorNotification('Failed to load tasks')
        }
      } catch (error) {
        console.error('Error loading todos:', error)
        this.displayErrorNotification('Error loading tasks')
      } finally {
        this.loading = false
      }
    },

    async loadAgents () {
      try {
        const agents = await DashboardService.getAgents()
        this.agents = Array.isArray(agents) ? agents : []
      } catch (error) {
        console.error('Error loading agents:', error)
        this.agents = []
      }
    },

    async loadCustomers () {
      try {
        let allCustomers = []
        let currentPage = 1
        let hasNextPage = true

        // Load all pages of customers
        while (hasNextPage) {
          const result = await DashboardService.getCustomers({
            per_page: 100,
            page: currentPage
          })

          console.log(`Loading customers page ${currentPage}:`, result)

          const customers = result.data?.customers || result.customers || []
          allCustomers = [...allCustomers, ...customers]

          // Check if there are more pages
          const pagination = result.data?.pagination || result.pagination || {}
          hasNextPage = pagination.has_next_page || false
          currentPage++

          // Safety check to prevent infinite loops
          if (currentPage > 20) {
            console.warn('Breaking pagination loop at page 20 for safety')
            break
          }
        }

        this.customers = allCustomers
        console.log(`Loaded ${allCustomers.length} total customers across ${currentPage - 1} pages`)
        console.log('First 5 customers:', allCustomers.slice(0, 5))
      } catch (error) {
        console.error('Error loading customers:', error)
        this.customers = []
      }
    },

    getAgentName (agentId) {
      if (!agentId) return 'Unassigned'
      const agent = this.agents.find(a => a.id === agentId)
      return agent ? agent.name : `Agent ${agentId}`
    },

    async saveTask () {
      try {
        this.saving = true

        let result
        if (this.showEditModal && this.editingTask) {
          result = await DashboardService.updateTodo(this.editingTask.id, this.taskForm)
        } else {
          result = await DashboardService.createTodo(this.taskForm)
        }

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadTodos()
          this.closeModal()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error saving task:', error)
        this.displayErrorNotification('Error saving task')
      } finally {
        this.saving = false
      }
    },

    async deleteTask (taskId) {
      if (!confirm('Are you sure you want to delete this task?')) return

      try {
        const result = await DashboardService.deleteTodo(taskId)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadTodos()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error deleting task:', error)
        this.displayErrorNotification('Error deleting task')
      }
    },

    async changeStatus (taskId, newStatus) {
      try {
        const result = await DashboardService.changeToDoStatus(taskId, newStatus)

        if (result.success) {
          this.displaySuccessNotification(result.message)
          await this.loadTodos()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error changing task status:', error)
        this.displayErrorNotification('Error updating task status')
      }
    },

    async togglePin (taskId) {
      try {
        const result = await DashboardService.toggleTodoPin(taskId)

        if (result.success) {
          await this.loadTodos()
        } else {
          this.displayErrorNotification(result.message)
        }
      } catch (error) {
        console.error('Error toggling pin:', error)
        this.displayErrorNotification('Error updating pin status')
      }
    },

    editTask (task) {
      this.editingTask = task
      this.taskForm = {
        title: task.title || '',
        description: task.description || '',
        priority: task.priority || 'medium',
        deadline: task.deadline || '',
        customer_id: task.customer_id || null,
        agent_id: task.agent_id || null,
        pinned: task.pinned || 0
      }

      // Set customer search query if task has a customer
      if (task.customer_name) {
        this.customerSearchQuery = task.customer_name
        this.selectedCustomer = {
          id: task.customer_id,
          full_name: task.customer_name,
          phone_number: task.customer_phone
        }
      } else {
        this.customerSearchQuery = ''
        this.selectedCustomer = null
      }

      this.showEditModal = true
    },

    closeModal () {
      this.showCreateModal = false
      this.showEditModal = false
      this.editingTask = null
      this.taskForm = {
        title: '',
        description: '',
        priority: 'medium',
        deadline: '',
        customer_id: null,
        agent_id: null,
        pinned: 0
      }
      this.customerSearchQuery = ''
      this.showCustomerDropdown = false
      this.selectedCustomer = null
    },

    onCustomerSearch () {
      this.showCustomerDropdown = true
    },

    selectCustomer (customer) {
      this.selectedCustomer = customer
      this.taskForm.customer_id = customer.id
      this.customerSearchQuery = customer.full_name
      this.showCustomerDropdown = false
    },

    handleCustomerBlur () {
      setTimeout(() => {
        this.showCustomerDropdown = false
      }, 200)
    },

    formatDate (dateString) {
      if (!dateString) return ''
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      })
    },

    getDeadlineStatus (deadline) {
      if (!deadline) return ''

      const now = new Date()
      const deadlineDate = new Date(deadline)
      const timeDiff = deadlineDate.getTime() - now.getTime()
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

      if (daysDiff < 0) {
        return `(${Math.abs(daysDiff)} days overdue)`
      } else if (daysDiff === 0) {
        return '(Due today)'
      } else if (daysDiff === 1) {
        return '(Due tomorrow)'
      } else if (daysDiff <= 30) {
        return `(${daysDiff} days left)`
      } else {
        const weeksDiff = Math.ceil(daysDiff / 7)
        if (weeksDiff <= 8) {
          return `(${weeksDiff} weeks left)`
        } else {
          const monthsDiff = Math.ceil(daysDiff / 30)
          return `(${monthsDiff} months left)`
        }
      }
    },

    getDeadlineUrgencyClass (deadline) {
      if (!deadline) return ''

      const now = new Date()
      const deadlineDate = new Date(deadline)
      const timeDiff = deadlineDate.getTime() - now.getTime()
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24))

      if (daysDiff < 0) {
        return 'deadline-overdue'
      } else if (daysDiff === 0) {
        return 'deadline-today'
      } else if (daysDiff <= 1) {
        return 'deadline-tomorrow'
      } else if (daysDiff <= 3) {
        return 'deadline-urgent'
      } else if (daysDiff <= 7) {
        return 'deadline-soon'
      }
      return 'deadline-normal'
    },

    displaySuccessNotification (message) {
      this.hideNotification()
      this.successMessage = message
      this.showSuccessNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 4000)
    },

    displayErrorNotification (message) {
      this.hideNotification()
      this.errorMessage = message
      this.showErrorNotification = true
      this.notificationTimeout = setTimeout(() => {
        this.hideNotification()
      }, 5000)
    },

    hideNotification () {
      this.showSuccessNotification = false
      this.showErrorNotification = false
      if (this.notificationTimeout) {
        clearTimeout(this.notificationTimeout)
        this.notificationTimeout = null
      }
    }
  },

  beforeUnmount () {
    if (this.notificationTimeout) {
      clearTimeout(this.notificationTimeout)
    }
  }
}
</script>

<style scoped>
.todo-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
  transition: background-color 0.3s ease;
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 32px;
}

.todo-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header-minimal {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 32px;
}

.btn-primary,
.btn-secondary {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
}

.btn-primary {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.btn-primary:hover:not(:disabled) {
  background: #2563eb;
  border-color: #2563eb;
}

.btn-primary:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

.btn-secondary {
  background: var(--bg-primary);
  color: var(--text-secondary);
  border-color: var(--border-primary);
  transition: all 0.3s ease;
}

.btn-secondary:hover:not(:disabled) {
  background: var(--bg-tertiary);
  border-color: var(--text-tertiary);
  color: var(--text-primary);
}

.btn-icon {
  font-size: 16px;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 64px;
  color: var(--text-secondary);
  text-align: center;
  transition: color 0.3s ease;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-primary);
  border-top: 3px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  transition: border-color 0.3s ease;
}

.loading-spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Kanban Board */
.kanban-board {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  align-items: start;
}

.kanban-column {
  background: var(--bg-primary);
  border-radius: 16px;
  border: 1px solid var(--border-primary);
  min-height: 600px;
  display: flex;
  flex-direction: column;
  transition: all 0.3s ease;
}

.todo-column {
  border-left: 4px solid #3b82f6;
}

.progress-column {
  border-left: 4px solid #f59e0b;
}

.completed-column {
  border-left: 4px solid #10b981;
}

.column-header {
  padding: 20px 24px 16px;
  border-bottom: 1px solid var(--border-secondary);
  transition: border-color 0.3s ease;
}

.column-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  transition: color 0.3s ease;
}

.column-icon {
  font-size: 20px;
}

.column-name {
  flex: 1;
}

.task-count {
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  font-size: 12px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 12px;
  min-width: 20px;
  text-align: center;
  transition: all 0.3s ease;
}

.todo-column .task-count {
  background: rgba(59, 130, 246, 0.1);
  color: #3b82f6;
}

.progress-column .task-count {
  background: rgba(245, 158, 11, 0.1);
  color: #f59e0b;
}

.completed-column .task-count {
  background: rgba(16, 185, 129, 0.1);
  color: #10b981;
}

.column-content {
  flex: 1;
  padding: 0 16px 16px;
}

.empty-column {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 48px 16px;
  color: var(--text-tertiary);
  text-align: center;
  transition: color 0.3s ease;
}

.empty-icon {
  font-size: 48px;
  opacity: 0.5;
}

.empty-column p {
  font-size: 14px;
  margin: 0;
}

.tasks-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 8px 0;
}

/* Task Cards */
.task-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s;
  cursor: pointer;
  box-shadow: 0 1px 3px var(--shadow-primary);
}

.task-card:hover {
  border-color: #3b82f6;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.1);
  transform: translateY(-1px);
}

.task-card.pinned {
  border-color: #f59e0b;
  background: var(--bg-primary);
  box-shadow: 0 0 0 1px rgba(245, 158, 11, 0.2), 0 1px 3px var(--shadow-primary);
  transition: all 0.3s ease;
}

.task-card.in-progress {
  border-color: #f59e0b;
}

.task-card.completed {
  opacity: 0.8;
  border-color: #10b981;
}

.task-card.urgent-deadline {
  border-left: 4px solid #dc2626;
  background: var(--bg-primary);
  box-shadow: 0 0 0 1px rgba(220, 38, 38, 0.2), 0 2px 8px rgba(220, 38, 38, 0.1);
}

.task-card.urgent-deadline:hover {
  border-color: #dc2626;
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.2);
}

.task-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.task-priority {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 4px;
}

.priority-low {
  background: #10b981;
}

.priority-medium {
  background: #3b82f6;
}

.priority-high {
  background: #f59e0b;
}

.priority-urgent {
  background: #ef4444;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}

.task-actions {
  display: flex;
  gap: 4px;
  opacity: 0.7;
  transition: opacity 0.2s;
}

.task-card:hover .task-actions {
  opacity: 1;
}

.task-btn {
  background: none;
  border: none;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
}

.task-btn:hover {
  background: var(--bg-tertiary);
}

.task-btn.pinned {
  color: #f59e0b;
  background: rgba(245, 158, 11, 0.1);
}

.task-btn.pinned svg {
  fill: #f59e0b;
}

.task-btn:not(.pinned) svg[stroke] {
  stroke: #9ca3af;
  opacity: 0.6;
}

.task-btn:not(.pinned):hover svg[stroke] {
  stroke: #6b7280;
  opacity: 1;
}

.task-btn.delete:hover {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.task-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
  line-height: 1.3;
  transition: color 0.3s ease;
}

.task-description {
  font-size: 14px;
  color: var(--text-secondary);
  margin: 0 0 12px;
  line-height: 1.4;
  transition: color 0.3s ease;
}

.task-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.task-meta {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
}

.task-due-date,
.task-assignee,
.task-customer {
  font-size: 12px;
  color: var(--text-secondary);
  display: flex;
  align-items: center;
  gap: 4px;
  transition: color 0.3s ease;
}

.task-due-date.completed-date {
  text-decoration: line-through;
}

/* Deadline Urgency Styles */
.task-due-date.deadline-overdue {
  color: #dc2626;
  font-weight: 600;
  background: rgba(220, 38, 38, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  animation: pulse-red 2s infinite;
}

.task-due-date.deadline-today {
  color: #ea580c;
  font-weight: 600;
  background: rgba(234, 88, 12, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
  animation: pulse-orange 2s infinite;
}

.task-due-date.deadline-tomorrow {
  color: #d97706;
  font-weight: 600;
  background: rgba(217, 119, 6, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.task-due-date.deadline-urgent {
  color: #ca8a04;
  font-weight: 500;
  background: rgba(202, 138, 4, 0.1);
  padding: 2px 6px;
  border-radius: 4px;
}

.task-due-date.deadline-soon {
  color: #65a30d;
  font-weight: 500;
}

.task-due-date.deadline-normal {
  color: #6b7280;
  font-weight: 400;
}

@keyframes pulse-red {
  0%, 100% {
    background: rgba(220, 38, 38, 0.1);
    transform: scale(1);
  }
  50% {
    background: rgba(220, 38, 38, 0.2);
    transform: scale(1.02);
  }
}

@keyframes pulse-orange {
  0%, 100% {
    background: rgba(234, 88, 12, 0.1);
  }
  50% {
    background: rgba(234, 88, 12, 0.2);
  }
}

.task-status-actions {
  display: flex;
  gap: 4px;
}

.status-btn {
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;
  opacity: 0.7;
}

.status-btn:hover {
  opacity: 1;
  background: var(--bg-tertiary);
}

.status-btn.progress:hover {
  background: rgba(245, 158, 11, 0.1);
}

.status-btn.complete:hover {
  background: rgba(16, 185, 129, 0.1);
}

.status-btn.back:hover {
  background: rgba(59, 130, 246, 0.1);
}

.status-btn.reopen:hover {
  background: rgba(245, 158, 11, 0.1);
}

/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  backdrop-filter: blur(4px);
}

.task-modal {
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 600px;
  width: 90%;
  margin: 20px;
  animation: modalSlideIn 0.3s ease-out;
  transition: background-color 0.3s ease;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(-10px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px 24px 16px;
  border-bottom: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
  transition: color 0.3s ease;
}

.modal-close {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 16px;
  color: var(--text-secondary);
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
}

.modal-close:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.task-form {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-bottom: 6px;
  transition: color 0.3s ease;
}

.form-input,
.form-textarea,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  color: var(--text-primary);
  background: var(--bg-primary);
  transition: all 0.2s;
  box-sizing: border-box;
}

.form-input:focus,
.form-textarea:focus,
.form-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select option {
  background: var(--bg-primary);
  color: var(--text-primary);
  padding: 8px 12px;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding-top: 20px;
  border-top: 1px solid var(--border-primary);
  transition: border-color 0.3s ease;
}

/* Searchable Dropdown Styles */
.searchable-dropdown {
  position: relative;
}

.dropdown-options {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  border-top: none;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  max-height: 200px;
  overflow-y: auto;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.dropdown-option {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;
  border-bottom: 1px solid var(--border-secondary);
  transition: background-color 0.2s ease;
}

.dropdown-option:last-child {
  border-bottom: none;
}

.dropdown-option:hover {
  background: var(--bg-tertiary);
}

.dropdown-option strong {
  color: var(--text-primary);
  font-size: 14px;
}

.customer-phone {
  color: var(--text-secondary);
  font-size: 12px;
}

.dropdown-no-results {
  padding: 12px 16px;
  color: var(--text-tertiary);
  font-size: 14px;
  text-align: center;
  font-style: italic;
}

/* Snackbar Notifications */
.snackbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  z-index: 3000;
  min-width: 300px;
  max-width: 500px;
  animation: slideUp 0.3s ease-out;
  backdrop-filter: blur(10px);
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateX(-50%) translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateX(-50%) translateY(0);
  }
}

.success-snackbar {
  background: linear-gradient(135deg, #10b981, #059669);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.error-snackbar {
  background: linear-gradient(135deg, #ef4444, #dc2626);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.snackbar-content {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.snackbar-icon {
  font-size: 18px;
  flex-shrink: 0;
}

.snackbar-message {
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
}

.snackbar-close {
  background: none;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 4px;
  border-radius: 4px;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0.8;
}

.snackbar-close:hover {
  opacity: 1;
  background: rgba(255, 255, 255, 0.1);
  transform: scale(1.1);
}

/* Mobile Responsive */
@media (max-width: 1024px) {
  .kanban-board {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  .kanban-column {
    min-height: auto;
  }
}

@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    padding-top: 64px;
  }

  .page-content {
    padding: 16px;
  }

  .page-header-minimal {
    justify-content: center;
    margin-bottom: 20px;
  }

  .btn-primary,
  .btn-secondary {
    flex: 1;
    justify-content: center;
  }

  .task-modal {
    width: 95%;
    margin: 10px;
  }

  .form-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }

  .modal-actions {
    flex-direction: column;
  }

  .snackbar {
    left: 16px;
    right: 16px;
    transform: none;
    min-width: auto;
    max-width: none;
  }

  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}

/* Drag and Drop Styles */
.task-card.dragging {
  opacity: 0.5;
  transform: rotate(5deg);
  cursor: grabbing;
  z-index: 1000;
}

.task-card[draggable="true"]:not(.dragging) {
  cursor: grab;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.task-card[draggable="true"]:not(.dragging):hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(59, 130, 246, 0.15);
}

.drop-zone {
  transition: all 0.3s ease;
  position: relative;
}

.drop-zone.drag-over {
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(59, 130, 246, 0.02) 100%);
  border-radius: 16px;
}

.drop-zone.drag-over::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 2px dashed #3b82f6;
  border-radius: 16px;
  pointer-events: none;
  animation: dashMove 1.5s linear infinite;
}

@keyframes dashMove {
  0% {
    border-color: #3b82f6;
    opacity: 0.8;
  }
  50% {
    border-color: #60a5fa;
    opacity: 1;
  }
  100% {
    border-color: #3b82f6;
    opacity: 0.8;
  }
}

.todo-column .drop-zone.drag-over::before {
  border-color: #3b82f6;
}

.progress-column .drop-zone.drag-over::before {
  border-color: #f59e0b;
}

.completed-column .drop-zone.drag-over::before {
  border-color: #10b981;
}
</style>
